/* Session: current user, login/logout, demo role switching. */
import { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { ApiError, useData } from "../data";
import { CLOCK_STALE_MS, qk } from "../data/hooks";
import type { LoginRequest, LoginResponse, Role, User } from "../data/types";

export interface Session {
  user: User | null;
  ready: boolean;
  role: Role | null;
  login(req: LoginRequest): Promise<User>;
  logout(): Promise<void>;
  switchRole(role: Role): Promise<User>;
  /** Start a session from a login response obtained elsewhere (email verification auto-login, password change).
   *  `keepCache` keeps cached queries (same user, new token). */
  adopt(r: LoginResponse, opts?: { keepCache?: boolean }): void;
}

const Ctx = createContext<Session | null>(null);

/** `initialUser`: when given (a user or null), the session starts ready with it and skips the async `me()` probe
 *  (tests / SSR-style bootstrapping). Omitted → the provider's token is validated with `me()`. */
export function SessionProvider({ children, initialUser }: { children: ReactNode; initialUser?: User | null }) {
  const p = useData();
  const qc = useQueryClient();
  const known = initialUser !== undefined;
  const [user, setUser] = useState<User | null>(initialUser ?? null);
  const [ready, setReady] = useState(known);

  useEffect(() => {
    if (known) return;
    let alive = true;
    /* Only a definitive 401 (expired/revoked/garbage token) drops the stored token. A network failure or an aborted
     * request (e.g. the page navigating away mid-probe) must not sign the user out of every future page load. */
    p.me().then((u) => { if (alive) setUser(u); })
      .catch((e: unknown) => { if (e instanceof ApiError && e.status === 401) p.setToken(null); })
      .finally(() => { if (alive) setReady(true); });
    return () => { alive = false; };
  }, [p, known]);

  /* Warm the server clock as soon as someone is signed in, so pages get the server's "today" on first render. */
  useEffect(() => {
    if (user) void qc.prefetchQuery({ queryKey: qk.clock, queryFn: () => p.clock(), staleTime: CLOCK_STALE_MS });
  }, [user, p, qc]);

  const login = useCallback(async (req: LoginRequest) => {
    const r = await p.login(req);
    qc.clear();
    setUser(r.user);
    return r.user;
  }, [p, qc]);
  const logout = useCallback(async () => {
    try { await p.logout(); } finally { qc.clear(); setUser(null); }
  }, [p, qc]);
  const switchRole = useCallback(async (role: Role) => {
    const r = await p.switchRole(role);
    qc.clear();
    setUser(r.user);
    return r.user;
  }, [p, qc]);

  const adopt = useCallback((r: LoginResponse, opts?: { keepCache?: boolean }) => {
    p.setToken(r.token);
    if (!opts?.keepCache) qc.clear();
    setUser(r.user);
    setReady(true);
  }, [p, qc]);

  const value = useMemo<Session>(() => ({ user, ready, role: user?.role ?? null, login, logout, switchRole, adopt }), [user, ready, login, logout, switchRole, adopt]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

export function useSession(): Session {
  const s = useContext(Ctx);
  if (!s) throw new Error("useSession() outside <SessionProvider>");
  return s;
}

/** Home route for a role. */
export const homeFor = (role: Role | null | undefined) => (role === "employee" ? "/ess" : role === "admin" ? "/admin" : "/dashboard");
